import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, computed, effect, inject, Inject, signal } from '@angular/core';
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '@core/authentication/auth.service';
import { ApplicationService, AttachmentService } from '@core/services';
import { OiqFormCheckboxComponent } from '@oiq-ui-sdk/form';
import { OiqUiAlertDialogService } from '@oiq-ui-sdk/ui';
import { OiqApplicationAddDialog } from '@v2/modules/applications/components/add/add-application.service';
import { OiqApplicationAddStore } from '@v2/modules/applications/components/add/add-application.store';
import {
  OIQ_APPLICATION_STIPULATIONS_PROVIDER,
  OIQ_APPLICATION_STIPULATIONS_PROVIDER_FACTORY,
} from '@v2/modules/applications/components/stipulations/stipulations.provider';
import { EmailAttachmentComponent } from '@v2/modules/email-app/components/email-attachment/email-attachment.component';
import { EmailPngPreviewComponent } from '@v2/modules/email-app/components/email-png-preview/email-png-preview.component';
import { EmailApplicationBridgeService } from '@v2/modules/email-app/services/email-application-bridge.service';
import { Attachment, EmailProvider } from '@v2/modules/email-app/services/email-oauth.service';
import { EmailOcrResult, EmailOcrService } from '@v2/modules/email-app/services/email-ocr.service';
import { EmailPreviewService } from '@v2/modules/email-app/services/email-preview.service';
import { OiqAttachmentsUploadDialog } from '@v2shared/components/attachments/attachments-upload.dialog';
import { OiqAttachmentModel } from '@v2shared/components/attachments/attachments-upload.models';
import { OiqAttachmentsUploadStore } from '@v2shared/components/attachments/attachments-upload.store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EmailLinkTarget } from '../../email-app.interfaces';
import { EMAIL_APP_STORE } from '../../email-app.store';
import { EmailOcrPreviewStateService } from '../../services/email-ocr-preview-state.service';

@Component({
  selector: 'oiq-save-client-documents-dialog',
  imports: [
    ReactiveFormsModule,
    OiqFormCheckboxComponent,
    EmailAttachmentComponent,
    EmailPngPreviewComponent,
  ],
  providers: [
    OIQ_APPLICATION_STIPULATIONS_PROVIDER_FACTORY,
    { provide: OiqAttachmentsUploadStore, useFactory: () => new OiqAttachmentsUploadStore() },
    { provide: OiqApplicationAddStore, useFactory: () => new OiqApplicationAddStore() },
    EmailOcrPreviewStateService,
  ],
  templateUrl: './save-client-documents-dialog.component.html',
  styleUrl: './save-client-documents-dialog.component.scss',
})
export class SaveClientDocumentsDialogComponent {
  private _stipulations = inject(OIQ_APPLICATION_STIPULATIONS_PROVIDER);
  private emailOcrService = inject(EmailOcrService);
  private emailPreviewService = inject(EmailPreviewService);
  private authService = inject(AuthService);
  private emailApplicationBridge = inject(EmailApplicationBridgeService);
  private attachmentService = inject(AttachmentService);
  private applicationService = inject(ApplicationService);
  private alertService = inject(OiqUiAlertDialogService);
  private ocrPreviewState = inject(EmailOcrPreviewStateService);
  private attachmentsUploadDialog = inject(OiqAttachmentsUploadDialog);
  private attachmentsUploadStore = inject(OiqAttachmentsUploadStore);
  private applicationDialog = inject(OiqApplicationAddDialog);
  private applicationStore = inject(OiqApplicationAddStore);
  private store = inject(EMAIL_APP_STORE);

  selectedFiles: FormArray<FormControl<boolean>>;
  selectAllControl: FormControl<boolean>;
  attachedStipulations = signal<
    {
      attachmentId: number;
      stipulationId: number;
    }[]
  >([]);

  ocrStatuses = this.ocrPreviewState.ocrStatuses;

  // Preview state
  showPngPreview = this.emailPreviewService.showPngPreviewNewApp;
  selectedPngFiles = this.emailPreviewService.selectedPngFilesNewApp;
  isProcessingOcr = signal<{ [key: string]: boolean }>({});
  ocrError = signal<string | null>(null);

  isAnyAttachmentProcessing = computed(() => {
    return Object.values(this.isProcessingOcr()).some(v => v === true);
  });

  // Selected file for new-application mode (only one can be selected)
  selectedFile = signal<any | null>(null);
  ocrResults = signal<{ [key: string]: EmailOcrResult }>({});

  // Loading state for saving stipulations
  isSavingStipulations = signal<boolean>(false);

  // Loading state for linking documents
  isLinkingDocuments = signal<boolean>(false);

  // Loading state for creating new application
  isCreatingApplication = signal<boolean>(false);

  constructor(
    public dialogRef: DialogRef<any, SaveClientDocumentsDialogComponent>,
    @Inject(DIALOG_DATA)
    public data: {
      attachments: any[];
      mode: 'link' | 'stipulations' | 'new-application';
      /** Fallback message id for rows that don't name one — used to fetch attachment content, not for linking */
      emailId?: string | null;
      /** Thread-level target for client/application linking */
      linkTarget?: EmailLinkTarget;
      provider?: EmailProvider;
      applicationId?: number | string;
      clientId?: number | string;
    }
  ) {
    // Initialize form controls for each attachment (all selected by default)
    this.selectedFiles = new FormArray(
      this.data.attachments.map(() => new FormControl<boolean>(true, { nonNullable: true }))
    );

    // Initialize select all control (all selected by default)
    this.selectAllControl = new FormControl<boolean>(true, { nonNullable: true });

    // Listen to select all changes
    this.selectAllControl.valueChanges.subscribe(selectAll => {
      this.selectedFiles.controls.forEach(control => {
        control.setValue(selectAll, { emitEvent: false });
      });
    });

    // Listen to individual checkbox changes to update select all state
    this.selectedFiles.valueChanges.subscribe(() => {
      const allSelected = this.selectedFiles.controls.every(control => control.value === true);
      const someSelected = this.selectedFiles.controls.some(control => control.value === true);

      // Update select all checkbox state (true if all selected, false if none selected, indeterminate if some)
      if (allSelected) {
        this.selectAllControl.setValue(true, { emitEvent: false });
      } else if (!someSelected) {
        this.selectAllControl.setValue(false, { emitEvent: false });
      }
      // If some are selected, we could set to indeterminate, but for simplicity, we'll leave it as false
    });

    // Set application ID for stipulations provider if available
    if (this.data.mode === 'stipulations' && this.data.applicationId) {
      const appId =
        typeof this.data.applicationId === 'string'
          ? parseInt(this.data.applicationId, 10)
          : this.data.applicationId;
      if (appId && !isNaN(appId)) {
        this._stipulations.application = { id: appId };
      }
    }

    effect(() => {
      const showPngPreviewNewApp = this.showPngPreview();

      if (!showPngPreviewNewApp) {
        this.ocrPreviewState.resetAllStatuses();
      }
    });
  }

  /**
   * Map attachments to the format expected by EmailAttachmentComponent
   */
  mappedAttachments = computed(() => {
    return this.data.attachments.map(attachment => ({
      name: attachment.filename || attachment.name,
      filename: attachment.filename || attachment.name,
      contentType: attachment.contentType,
      data: attachment.data,
      size: attachment.size,
      id: attachment.id || attachment.filename || attachment.name,
      emailId: attachment.emailId ?? this.data.emailId ?? undefined,
      sourceLabel: attachment.sourceLabel ?? '',
    }));
  });

  attachmentGroups = computed(() => {
    const groups: {
      key: string;
      label: string;
      items: { attachment: ReturnType<typeof this.mappedAttachments>[number]; index: number }[];
    }[] = [];

    this.mappedAttachments().forEach((attachment, index) => {
      const key = attachment.emailId || attachment.sourceLabel || '';
      const current = groups[groups.length - 1];
      if (current && current.key === key) {
        current.items.push({ attachment, index });
        return;
      }
      groups.push({ key, label: attachment.sourceLabel, items: [{ attachment, index }] });
    });

    return groups;
  });

  /** Only worth labelling the groups when the list actually spans several messages. */
  showAttachmentGroupLabels = computed(
    () => this.attachmentGroups().length > 1 && this.attachmentGroups().some(g => !!g.label)
  );

  cancel() {
    this.dialogRef.close();
    this.emailPreviewService.closePngPreviewNewApp();
  }

  submit() {
    this.dialogRef.close(this.data.attachments);
  }

  /**
   * Check if any files are selected
   */
  hasSelectedFiles(): boolean {
    return this.selectedFiles.controls.some(control => control.value === true);
  }

  /**
   * Confirm and link selected documents to the application
   * First uploads email attachments, then links them to the application
   */
  confirmLinkDocuments(): void {
    const selectedAttachments = this.data.attachments.filter(
      (_, index) => this.selectedFiles.at(index).value
    );

    if (selectedAttachments.length === 0) {
      return;
    }

    if (!this.data.applicationId) {
      alert('Please link this email to an application first.');
      return;
    }

    const appId =
      typeof this.data.applicationId === 'string'
        ? parseInt(this.data.applicationId, 10)
        : this.data.applicationId;

    if (!appId || isNaN(appId)) {
      alert('Invalid application ID. Please link this email to an application first.');
      return;
    }

    // Set loading state
    this.isLinkingDocuments.set(true);

    // Upload all selected attachments and get server-side IDs
    const uploadObservables = selectedAttachments.map(attachment =>
      this.uploadAttachmentToServer(attachment)
    );

    forkJoin(uploadObservables)
      .pipe(
        catchError(error => {
          this.isLinkingDocuments.set(false);
          this.alertService.open(
            {
              title: 'Upload failed',
              content: 'Failed to upload attachments. Please try again.',
              type: 'error',
              buttonBehaviour: 'close',
              buttons: [
                {
                  id: 'close',
                  text: 'Close',
                  style: 'primary-solid',
                  behaviour: 'close',
                },
              ],
            },
            {}
          );
          return of([]);
        })
      )
      .subscribe(uploadedFiles => {
        // Filter out failed uploads (null values)
        const successfulUploads = uploadedFiles.filter(file => file !== null);

        if (successfulUploads.length === 0) {
          this.isLinkingDocuments.set(false);
          this.alertService.open(
            {
              title: 'Upload failed',
              content: 'Failed to upload attachments. Please try again.',
              type: 'error',
              buttonBehaviour: 'close',
              buttons: [
                {
                  id: 'close',
                  text: 'Close',
                  style: 'primary-solid',
                  behaviour: 'close',
                },
              ],
            },
            {}
          );
          return;
        }

        // Link uploaded files to the application
        const serverAttachmentIds = successfulUploads.map(file => file.id).filter(id => !!id);

        if (serverAttachmentIds.length === 0) {
          this.isLinkingDocuments.set(false);
          return;
        }

        this.applicationService.addAttachments(appId, serverAttachmentIds).subscribe({
          next: () => {
            // Reset loading state before closing
            this.isLinkingDocuments.set(false);

            // Close dialog with success
            this.dialogRef.close({
              linked: true,
              count: serverAttachmentIds.length,
              attachmentIds: serverAttachmentIds,
            });

            // Close PNG preview if open
            this.emailPreviewService.closePngPreviewNewApp();
          },
          error: error => {
            this.isLinkingDocuments.set(false);
            this.alertService.open(
              {
                title: 'Link failed',
                content: 'Failed to link attachments to application. Please try again.',
                type: 'error',
                buttonBehaviour: 'close',
                buttons: [
                  {
                    id: 'close',
                    text: 'Close',
                    style: 'primary-solid',
                    behaviour: 'close',
                  },
                ],
              },
              {}
            );
          },
        });
      });
  }

  /**
   * Download selected files (legacy method, kept for backwards compatibility if needed)
   */
  downloadSelected(): void {
    const selectedAttachments = this.data.attachments.filter(
      (_, index) => this.selectedFiles.at(index).value
    );

    if (selectedAttachments.length === 0) {
      return;
    }

    // Download each selected attachment
    selectedAttachments.forEach(attachment => {
      this.downloadAttachment(attachment);
    });

    // Close dialog after initiating downloads
    this.dialogRef.close({ downloaded: true, count: selectedAttachments.length });
  }

  /**
   * Download a single attachment
   */
  private downloadAttachment(attachment: any): void {
    if (!attachment.data) {
      return;
    }

    try {
      // Convert base64 to blob
      const base64 = attachment.data.replace(/^data:[^;]+;base64,/, '');
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: attachment.contentType || 'application/octet-stream',
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename || attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Error downloading attachment
    }
  }

  /**
   * Confirm and associate attachment-stipulation pairs
   * First uploads email attachments, then associates them with stipulations
   */
  confirmAttachStipulations(): void {
    const pairs = this.attachedStipulations();

    if (!pairs || pairs.length === 0) {
      this.dialogRef.close();
      return;
    }

    if (!this.data.applicationId) {
      return;
    }

    const appId =
      typeof this.data.applicationId === 'string'
        ? parseInt(this.data.applicationId, 10)
        : this.data.applicationId;

    if (!appId || isNaN(appId)) {
      return;
    }

    // Set loading state
    this.isSavingStipulations.set(true);

    // Step 1: Get all unique attachments from the pairs (by email attachment ID)
    // Note: attachmentId in pairs could be a string (filename) or number
    const attachmentIdsInPairs = [...new Set(pairs.map(pair => pair.attachmentId))];
    const attachmentsToUpload = this.mappedAttachments().filter(att => {
      // Match by id, filename, or name (to handle different ID formats)
      const attId = att.id;
      const attFilename = att.filename || att.name;
      return (
        attachmentIdsInPairs.includes(attId) ||
        attachmentIdsInPairs.includes(attFilename) ||
        (typeof attId === 'string' && attachmentIdsInPairs.some(id => String(id) === attId)) ||
        (typeof attId === 'number' && attachmentIdsInPairs.some(id => Number(id) === attId))
      );
    });

    if (attachmentsToUpload.length === 0) {
      alert('Error: Could not find attachments to upload.');
      return;
    }

    // Step 2: Upload all attachments and get server-side IDs
    const uploadObservables = attachmentsToUpload.map(attachment =>
      this.uploadAttachmentToServer(attachment)
    );

    forkJoin(uploadObservables)
      .pipe(
        catchError(error => {
          this.alertService.open(
            {
              title: 'Upload failed',
              content: 'Failed to upload attachments. Please try again.',
              type: 'error',
              buttonBehaviour: 'close',
              buttons: [
                {
                  id: 'close',
                  text: 'Close',
                  style: 'primary-solid',
                  behaviour: 'close',
                },
              ],
            },
            {}
          );
          return of([]);
        })
      )
      .subscribe(uploadedFiles => {
        // Filter out failed uploads (null values)
        const successfulUploads = uploadedFiles.filter(file => file !== null);

        if (successfulUploads.length === 0) {
          this.alertService.open(
            {
              title: 'Upload failed',
              content: 'Failed to upload attachments. Please try again.',
              type: 'error',
              buttonBehaviour: 'close',
              buttons: [
                {
                  id: 'close',
                  text: 'Close',
                  style: 'primary-solid',
                  behaviour: 'close',
                },
              ],
            },
            {}
          );
          return;
        }

        // Step 3: Create mapping from email attachment ID to server attachment ID
        // Match pairs to uploaded files by their order and create mapping
        const idMapping = new Map<string | number, number>();
        attachmentsToUpload.forEach((emailAtt, index) => {
          const uploadedFile = successfulUploads[index];
          if (uploadedFile && uploadedFile.id) {
            // Map both the id and filename/name to handle different ID formats
            const attId = emailAtt.id;
            const attFilename = emailAtt.filename || emailAtt.name;
            if (attId) idMapping.set(attId, uploadedFile.id);
            if (attFilename) idMapping.set(attFilename, uploadedFile.id);
          }
        });

        // Step 4: Update pairs with server-side attachment IDs
        const serverPairs = pairs
          .map(pair => {
            // Try to find server ID by the pair's attachmentId (could be string or number)
            const serverAttachmentId =
              idMapping.get(pair.attachmentId) ||
              idMapping.get(String(pair.attachmentId)) ||
              idMapping.get(Number(pair.attachmentId));
            if (!serverAttachmentId) {
              return null;
            }
            return {
              attachmentId: serverAttachmentId,
              stipulationId: pair.stipulationId,
            };
          })
          .filter(pair => pair !== null) as { attachmentId: number; stipulationId: number }[];

        if (serverPairs.length === 0) {
          this.isSavingStipulations.set(false);
          alert('Error: Could not associate attachments with stipulations.');
          return;
        }

        // Step 5: Associate uploaded files with the application
        const serverAttachmentIds = [...new Set(serverPairs.map(p => p.attachmentId))];
        this.applicationService.addAttachments(appId, serverAttachmentIds).subscribe({
          next: () => {
            // Step 6: Associate attachment-stipulation pairs
            this._stipulations.associateAttachmentStipulationPairs(serverPairs);

            // Reset loading state before closing
            this.isSavingStipulations.set(false);

            // Close dialog
            this.dialogRef.close({ confirmed: true, pairs: serverPairs });

            // Close PNG preview if open
            this.emailPreviewService.closePngPreviewNewApp();
          },
          error: error => {
            this.isSavingStipulations.set(false);
            this.alertService.open(
              {
                title: 'Association failed',
                content: 'Failed to associate attachments with application. Please try again.',
                type: 'error',
                buttonBehaviour: 'close',
                buttons: [
                  {
                    id: 'close',
                    text: 'Close',
                    style: 'primary-solid',
                    behaviour: 'close',
                  },
                ],
              },
              {}
            );
          },
        });
      });
  }

  /**
   * Check if attachment can be processed with OCR
   */
  canProcessWithOcr(attachment: any): boolean {
    if (!attachment.filename && !attachment.name) return false;
    const filename = (attachment.filename || attachment.name).toLowerCase();
    const supportedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];
    return supportedExtensions.some(ext => filename.endsWith(ext));
  }

  /**
   * Handle document click - process with OCR and show preview
   */
  onPreviewClick(attachment: any, event: MouseEvent): void {
    // Prevent click if clicking on buttons
    // const target = event.target as HTMLElement;
    // if (target.closest('button')) {
    //   return;
    // }

    event.stopPropagation();

    // Only process PDFs and supported image formats
    if (!this.canProcessWithOcr(attachment)) {
      return;
    }

    const attachmentId = attachment.id || attachment.filename || attachment.name;

    // Check if already processing
    if (this.isProcessingOcr()[attachmentId]) {
      return;
    }

    // Convert to Attachment format
    const ocrAttachment: Attachment = {
      Filename: attachment.filename || attachment.name,
      ContentType: attachment.contentType || 'application/pdf',
      Data: attachment.data || '',
      Size: attachment.size || 0,
    };

    if (!ocrAttachment.Data) {
      return;
    }

    const tenantId = this.authService.getUser().tenantId;

    // Set processing state
    this.isProcessingOcr.update(state => ({ ...state, [attachmentId]: true }));
    this.ocrError.set(null);

    // Process with OCR
    this.emailOcrService.processEmailAttachment(ocrAttachment, tenantId).subscribe({
      next: result => {
        this.isProcessingOcr.update(state => ({ ...state, [attachmentId]: false }));

        // Open preview with PNG files
        if (result.pngFiles && result.pngFiles.length > 0) {
          this.emailPreviewService.openPngPreviewNewApp(result.pngFiles);
        }
      },
      error: error => {
        this.isProcessingOcr.update(state => ({ ...state, [attachmentId]: false }));
        this.ocrError.set(error);
      },
    });
  }

  /**
   * Check if attachment is currently being processed
   */
  isAttachmentProcessing(attachment: any): boolean {
    const attachmentId = attachment.id || attachment.filename || attachment.name;
    return this.isProcessingOcr()[attachmentId] || false;
  }

  /**
   * Check if attachment is selected
   */
  isFileSelected(attachment: any): boolean {
    const selected = this.selectedFile();
    if (!selected) return false;
    const attachmentId = attachment.id || attachment.filename || attachment.name;
    const selectedId = selected.id || selected.filename || selected.name;
    return attachmentId === selectedId;
  }

  /**
   * Handle Select button click - process with OCR, show preview, and select file
   */
  onSelectFile(attachment: any, event: MouseEvent): void {
    event.stopPropagation();

    // Only process PDFs and supported image formats
    if (!this.canProcessWithOcr(attachment)) {
      return;
    }

    const attachmentId = attachment.id || attachment.filename || attachment.name;

    // Check if already processing
    if (this.isProcessingOcr()[attachmentId]) {
      return;
    }

    // Check if already selected
    if (this.isFileSelected(attachment)) {
      // If already selected and processed, open OCR dialog
      const ocrResult = this.ocrResults()[attachmentId];
      if (ocrResult) {
        this.openOcrMatchDialog(ocrResult);
      }
      return;
    }

    // Deselect previous file (only one can be selected)
    this.selectedFile.set(null);

    // Convert to Attachment format
    const ocrAttachment: Attachment = {
      Filename: attachment.filename || attachment.name,
      ContentType: attachment.contentType || 'application/pdf',
      Data: attachment.data || '',
      Size: attachment.size || 0,
    };

    if (!ocrAttachment.Data) {
      return;
    }

    const tenantId = this.authService.getUser().tenantId;

    // Set processing state
    this.isProcessingOcr.update(state => ({ ...state, [attachmentId]: true }));

    // Process with OCR
    this.emailOcrService.processEmailAttachment(ocrAttachment, tenantId).subscribe({
      next: result => {
        this.isProcessingOcr.update(state => ({ ...state, [attachmentId]: false }));

        // Store OCR result
        this.ocrResults.update(results => ({ ...results, [attachmentId]: result }));

        // Select the file
        this.selectedFile.set(attachment);

        // Open preview with PNG files
        if (result.pngFiles && result.pngFiles.length > 0) {
          this.emailPreviewService.openPngPreviewNewApp(result.pngFiles);
        }

        // Open OCR match dialog
        this.openOcrMatchDialog(result);
      },
      error: error => {
        this.isProcessingOcr.update(state => ({ ...state, [attachmentId]: false }));
      },
    });
  }

  /**
   * Open OCR match dialog by navigating to add-application with bridge data
   */
  openOcrMatchDialog(ocrResult: EmailOcrResult): void {
    // Close this dialog first
    this.dialogRef.close({ ocrProcessed: true });

    // Navigate to add-application with OCR data using the bridge service
    // This will open the OCR match dialog in the add-application context
    this.emailApplicationBridge.createApplicationFromEmail(ocrResult);
  }

  /**
   * Handle confirm button click for new-application mode
   * Uploads selected attachments and opens the application dialog flow
   */
  confirmNewApplication(): void {
    // Get selected attachments
    const selectedAttachments = this.data.attachments.filter(
      (_, index) => this.selectedFiles.at(index).value
    );

    // Set loading state
    this.isCreatingApplication.set(true);

    if (selectedAttachments.length === 0) {
      this.openApplicationUploadDialog([]);
      return;
    }

    // Upload all selected attachments
    const uploadObservables = selectedAttachments.map((attachment, index) => {
      return this.uploadAttachmentToServer(attachment);
    });

    // Execute all uploads in parallel
    forkJoin(uploadObservables)
      .pipe(
        catchError(error => {
          this.isCreatingApplication.set(false);
          this.alertService.open(
            {
              title: 'Upload failed',
              content: 'Failed to upload attachments. Please try again.',
              type: 'error',
              buttonBehaviour: 'close',
              buttons: [
                {
                  id: 'close',
                  text: 'Close',
                  style: 'primary-solid',
                  behaviour: 'close',
                },
              ],
            },
            {}
          );
          return of(null);
        })
      )
      .subscribe(uploadedFiles => {
        if (!uploadedFiles) {
          this.isCreatingApplication.set(false);
          return;
        }

        // Filter out any failed uploads (null values)
        const successfulUploads = uploadedFiles.filter(file => file !== null);

        if (successfulUploads.length === 0) {
          this.isCreatingApplication.set(false);
          this.alertService.open(
            {
              title: 'Upload failed',
              content: 'Failed to upload attachments. Please try again.',
              type: 'error',
              buttonBehaviour: 'close',
              buttons: [
                {
                  id: 'close',
                  text: 'Close',
                  style: 'primary-solid',
                  behaviour: 'close',
                },
              ],
            },
            {}
          );
          return;
        }

        // Convert uploaded files to OiqAttachmentModel format
        const attachmentModels: OiqAttachmentModel[] = successfulUploads.map((file: any) => ({
          id: file.id,
          filename: file.filename || file.name,
          size: file.size || 0,
          location: file.location || '',
          type: file.type || 'application',
          category: file.category || 'generic',
          createdDate: file.createdDate || Date.now(),
        }));

        this.openApplicationUploadDialog(attachmentModels);
      });
  }

  private openApplicationUploadDialog(attachmentModels: OiqAttachmentModel[]): void {
    // Reset store before adding new files to prevent duplicates
    this.attachmentsUploadStore.reset();

    // Set tenant ID on the store
    this.attachmentsUploadStore.tenantId = this.authService.getUser().tenantId;
    this.attachmentsUploadStore.type = 'application';

    if (attachmentModels.length > 0) {
      this.attachmentsUploadStore.add(attachmentModels);
    }

    // Close PNG preview
    this.emailPreviewService.closePngPreviewNewApp();

    // Open the attachments upload dialog
    this.applicationDialog
      .open({
        mode: 'upload',
        applicationStore: this.applicationStore,
        attachmentsStore: this.attachmentsUploadStore,
        formData: {
          clientId: this.data.clientId,
        },
      })
      .closed.subscribe((result: any) => {
        if (!result) {
          this.isCreatingApplication.set(false);
          return;
        }

        // Handle dialog result based on action
        switch (result.action) {
          case 'create':
            this.store
              .linkEmailToClientAndApplication(
                this.data.linkTarget ?? { emailId: this.data.emailId ?? undefined },
                result.data.clientId,
                result.data.id
              )
              .subscribe({
                next: linkDto => {
                  // Reset loading state before closing
                  this.isCreatingApplication.set(false);
                  this.dialogRef.close({ uploaded: true });
                },
                error: error => {
                  this.isCreatingApplication.set(false);
                },
              });
            break;
          default:
            this.isCreatingApplication.set(false);
            break;
        }
      });
  }

  /**
   * Upload a single attachment to the server
   */
  private uploadAttachmentToServer(attachment: any) {
    // Handle both lowercase and capitalized property names
    let attachmentData = attachment.data || attachment.Data;
    const attachmentFilename = attachment.filename || attachment.name || attachment.Filename;
    const attachmentContentType =
      attachment.contentType || attachment.ContentType || 'application/octet-stream';

    // If attachment data is missing (lazy loaded attachments might not have Data),
    // we need to fetch it from the backend using emailId and provider. The row's own
    // message wins over the dialog-level fallback — a thread list mixes several.
    const sourceEmailId = attachment.emailId ?? this.data.emailId;
    if (!attachmentData && sourceEmailId && this.data.provider) {
      // Note: We can't easily fetch individual attachment data here without the attachment ID
      // The backend would need an endpoint like /emails/{emailId}/attachments/{attachmentId}/content
      // For now, we'll return an error observable
      return of(null);
    }

    if (!attachmentData) {
      return of(null);
    }

    if (!attachmentFilename) {
      return of(null);
    }

    try {
      // Convert base64 to blob
      // Handle both data URL format and raw base64
      let base64 = attachmentData;
      if (base64.includes(',')) {
        // Remove data URL prefix if present (e.g., "data:application/pdf;base64,...")
        base64 = base64.split(',')[1];
      } else if (base64.startsWith('data:')) {
        base64 = base64.replace(/^data:[^;]+;base64,/, '');
      }

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: attachmentContentType,
      });

      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', blob, attachmentFilename);

      const tenantId = this.authService.getUser().tenantId;

      if (!tenantId) {
        return of(null);
      }

      // Upload to server
      return this.attachmentService.upload(formData, 'application', 'generic', tenantId).pipe(
        catchError(() => {
          return of(null);
        })
      );
    } catch (error: any) {
      return of(null);
    }
  }

  onOcrPreviewRequest(attachmentId: string, request$: Observable<EmailOcrResult>) {
    this.ocrPreviewState.requestOcrPreview(attachmentId, request$);
  }

  onCloseOcrPreview(attachmentId: string) {
    this.ocrPreviewState.closeOcrPreview(attachmentId);
  }
}
